import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, CheckCircle2, Circle, ExternalLink, LogOut, MessageCircle, MousePointerClick, Send, LayoutGrid, Users, Shield, Images, ShieldCheck } from "lucide-react";
import SiteForm from "../components/SiteForm";
import AgentsPanel from "../components/AgentsPanel";
import BrandsPanel from "../components/BrandsPanel";
import SecurityDialog from "../components/SecurityDialog";

const AdminDashboard = ({ sites, user, onCreate, onUpdate, onDelete, onSetActive, onLogout, onUserRefresh }) => {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("sites");
  const [securityOpen, setSecurityOpen] = useState(false);
  const isSuper = user?.role === "super_admin";

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (site) => {
    setEditing(site);
    setFormOpen(true);
  };

  const handleSave = (payload) => {
    if (editing) onUpdate(editing.id, payload);
    else onCreate(payload);
    setFormOpen(false);
  };

  const logout = () => {
    onLogout();
    navigate("/admin/login");
  };

  return (
    <div className="dot-grid min-h-screen">
      <header className="border-b border-white/10 bg-[#1b1d2b]/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => { setTab("sites"); navigate("/admin"); }}
            className="flex items-center hover:opacity-90 transition-opacity"
            title="Home"
          >
            <img src="/logos/max66-header.png" alt="MAX66" className="h-12 sm:h-14 w-auto object-contain" />
          </button>
          <div className="flex items-center gap-3">
            {user?.email && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/60 bg-white/5 px-3 py-1.5 rounded-full">
                {isSuper && <Shield className="w-3.5 h-3.5 text-[#25D366]" />}
                {user.email}
              </span>
            )}
            <button onClick={() => setSecurityOpen(true)} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5 transition-colors">
              <ShieldCheck className="w-4 h-4" /> Security
            </button>
            <button onClick={() => navigate("/")} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5 transition-colors">
              <ExternalLink className="w-4 h-4" /> View Site
            </button>
            <button onClick={logout} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
            <button onClick={() => setTab("sites")} className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${tab === "sites" ? "bg-[#25D366] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
              <LayoutGrid className="w-4 h-4" /> Sites
            </button>
            <button onClick={() => setTab("brands")} className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${tab === "brands" ? "bg-[#25D366] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
              <Images className="w-4 h-4" /> Featured Brands
            </button>
            {isSuper && (
              <button onClick={() => setTab("team")} className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${tab === "team" ? "bg-[#25D366] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
                <Users className="w-4 h-4" /> Team
              </button>
            )}
          </div>

        {tab === "team" && isSuper ? (
          <AgentsPanel />
        ) : tab === "brands" ? (
          <BrandsPanel />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display font-bold text-2xl text-white">Sites</h1>
                <p className="text-white/50 text-sm mt-0.5">Manage brands, WhatsApp & Telegram links</p>
              </div>
              <button onClick={openAdd} className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">
                <Plus className="w-4 h-4" /> Add Site
              </button>
            </div>

            <div className="grid gap-4">
          {sites.map((site) => (
            <div key={site.id} className={`bg-[#1b1d2b] border rounded-xl p-5 transition-colors ${site.active ? "border-[#25D366]/60" : "border-white/10"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-12 h-12 shrink-0 rounded-full p-[2px]" style={{ background: site.accentColor || "#f6c453" }}>
                    <div className="badge-inner rounded-full w-full h-full flex items-center justify-center overflow-hidden">
                      {site.logo ? (
                        <img src={site.logo} alt={site.name} className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <span className="font-display font-extrabold text-sm" style={{ color: site.accentColor || "#f6c453" }}>{site.initials}</span>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-white">{site.name}</h3>
                      {site.active && (
                        <span className="text-[10px] font-bold uppercase text-[#25D366] bg-[#25D366]/15 px-2 py-0.5 rounded-full">Active</span>
                      )}
                      <span className="text-[10px] font-semibold text-white/70 bg-white/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <MousePointerClick className="w-3 h-3" /> {site.clicks || 0} clicks
                      </span>
                    </div>
                    <p className="text-white/50 text-xs mt-0.5 truncate">{site.tagline}</p>
                    <div className="flex flex-col gap-0.5 mt-1.5">
                      <a href={site.whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-[#25D366] text-xs inline-flex items-center gap-1 hover:underline truncate">
                        <MessageCircle className="w-3 h-3" /> {site.whatsappUrl}
                      </a>
                      {site.telegramUrl && (
                        <a href={site.telegramUrl} target="_blank" rel="noopener noreferrer" className="text-[#229ED9] text-xs inline-flex items-center gap-1 hover:underline truncate">
                          <Send className="w-3 h-3" /> {site.telegramUrl}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button title={site.active ? "Active" : "Set active"} onClick={() => !site.active && onSetActive(site.id)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                    {site.active ? <CheckCircle2 className="w-5 h-5 text-[#25D366]" /> : <Circle className="w-5 h-5 text-white/40" />}
                  </button>
                  <button onClick={() => openEdit(site)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                    <Pencil className="w-4 h-4 text-white/70" />
                  </button>
                  <button onClick={() => onDelete(site.id)} className="p-2 rounded-lg hover:bg-red-500/15 transition-colors">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {sites.length === 0 && (
            <div className="text-center py-16 text-white/40">No sites yet. Add your first one.</div>
          )}
            </div>
          </>
        )}
      </main>

      <SiteForm open={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave} initial={editing} />
      <SecurityDialog open={securityOpen} onClose={() => setSecurityOpen(false)} user={user} onUpdated={onUserRefresh} />
    </div>
  );
};

export default AdminDashboard;
